import React, { useState } from 'react';
import useAppleAnimation from '../hooks/useAppleAnimation';

/**
 * TaxBenefitsExamples Component
 * 소득 공제혜택 실제 사례를 보여주는 섹션
 * 다양한 소득 구간별 실제 절세 효과를 카드 형태로 표시
 * TaxCalculator의 계산 로직을 사용하여 정확한 절세액 표시
 */
const TaxBenefitsExamples = () => {
  // 애니메이션 효과를 위한 ref
  const fadeInRef = useAppleAnimation('apple-slide-up');
  const cardsRef = useAppleAnimation('staggered-animation');

  // 모달 상태 관리
  const [isModalOpen, setIsModalOpen] = useState(false);

  // TaxCalculator와 동일한 세금 계산 함수 (2025년 기준)
  const calculateIncomeTax = (taxableIncome) => {
    const brackets = [
      { limit: 14000000, rate: 0.06, deduction: 0 },
      { limit: 50000000, rate: 0.15, deduction: 1260000 },
      { limit: 88000000, rate: 0.24, deduction: 5760000 },
      { limit: 150000000, rate: 0.35, deduction: 15440000 },
      { limit: 300000000, rate: 0.38, deduction: 19940000 },
      { limit: 500000000, rate: 0.40, deduction: 25940000 },
      { limit: 1000000000, rate: 0.42, deduction: 35940000 },
      { limit: Infinity, rate: 0.45, deduction: 65940000 }
    ];

    for (const bracket of brackets) {
      if (taxableIncome <= bracket.limit) {
        return Math.max(0, taxableIncome * bracket.rate - bracket.deduction);
      }
    }
    return 0;
  };

  // 벤처투자 소득공제 계산 (TaxCalculator와 동일)
  const calculateVentureDeduction = (investment, income) => {
    let deduction = 0;
    if (investment <= 30000000) {
      deduction = investment;
    } else if (investment <= 50000000) {
      deduction = 30000000 + (investment - 30000000) * 0.7;
    } else {
      deduction = 30000000 + 20000000 * 0.7 + (investment - 50000000) * 0.3;
    }

    // 한도: 종합소득금액의 50% 이내
    const basicDeduction = 1500000; // 기본공제 150만원
    const maxTaxableIncome = Math.max(0, income - basicDeduction);

    return Math.min(deduction, income * 0.5, maxTaxableIncome);
  };

  // 실제 절세액 계산
  const calculateTaxSavings = (comprehensiveIncome, taxableIncome, investment) => {
    // 투자 전 세금
    const beforeIncomeTax = calculateIncomeTax(taxableIncome);
    const beforeLocalTax = beforeIncomeTax * 0.1;
    const beforeTotalTax = beforeIncomeTax + beforeLocalTax;

    // 소득공제 금액 계산
    const ventureDeduction = calculateVentureDeduction(investment, comprehensiveIncome);

    // 투자 후 과세표준
    const afterTaxableIncome = Math.max(0, taxableIncome - ventureDeduction);

    // 투자 후 세금
    const afterIncomeTax = calculateIncomeTax(afterTaxableIncome);
    const afterLocalTax = afterIncomeTax * 0.1;
    const afterTotalTax = afterIncomeTax + afterLocalTax;

    // 절세액
    const taxSavings = beforeTotalTax - afterTotalTax;

    return {
      deduction: Math.round(ventureDeduction / 10000), // 만원 단위
      taxSavings: Math.round(taxSavings / 10000) // 만원 단위
    };
  };

  // 실제 사례 데이터 with 정확한 계산
  const casesRaw = [
    {
      title: "직장인 A씨",
      annualIncome: "연소득 5천만원",
      comprehensiveIncome: 50000000, // 종합소득금액 5천만원
      taxableIncome: 35000000, // 과세표준 약 3,500만원 (각종 공제 후)
      investment: "2,500만원 투자",
      investmentAmount: 25000000, // 원 단위
      color: "blue",
      icon: (
        <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"/>
        </svg>
      )
    },
    {
      title: "전문직 B씨",
      annualIncome: "연소득 1억원",
      comprehensiveIncome: 100000000, // 종합소득금액 1억원
      taxableIncome: 85000000, // 과세표준 약 8,500만원
      investment: "5천만원 투자",
      investmentAmount: 50000000, // 원 단위
      color: "purple",
      icon: (
        <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/>
        </svg>
      )
    },
    {
      title: "사업자 C씨",
      annualIncome: "연소득 2억원",
      comprehensiveIncome: 200000000, // 종합소득금액 2억원
      taxableIncome: 180000000, // 과세표준 약 1.8억원
      investment: "1억원 투자",
      investmentAmount: 100000000, // 원 단위
      color: "green",
      icon: (
        <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"/>
        </svg>
      )
    }
  ];

  // 각 사례에 대해 실제 계산 수행
  const cases = casesRaw.map(caseData => {
    const result = calculateTaxSavings(
      caseData.comprehensiveIncome,
      caseData.taxableIncome,
      caseData.investmentAmount
    );

    return {
      ...caseData,
      taxableIncomeInManwon: caseData.taxableIncome / 10000, // 과세표준 만원 단위
      deduction: result.deduction,
      taxSavings: result.taxSavings,
      investmentAmountInManwon: caseData.investmentAmount / 10000 // 만원 단위로 저장
    };
  });

  // 색상별 그라디언트 스타일 매핑
  const getColorClasses = (color) => {
    const colorMap = {
      blue: {
        gradient: 'from-blue-500 to-blue-600',
        light: 'from-blue-50 to-blue-100',
        text: 'text-blue-600',
        bg: 'bg-blue-500'
      },
      purple: {
        gradient: 'from-purple-500 to-purple-600',
        light: 'from-purple-50 to-purple-100',
        text: 'text-purple-600',
        bg: 'bg-purple-500'
      },
      green: {
        gradient: 'from-green-500 to-green-600',
        light: 'from-green-50 to-green-100',
        text: 'text-green-600',
        bg: 'bg-green-500'
      }
    };
    return colorMap[color];
  };

  return (
    // 섹션 컨테이너
    // py-20: 상하 패딩 5rem (80px), 섹션 간 충분한 여백 확보
    // bg-gradient-to-b: 위에서 아래로 그라디언트 배경
    <section className="py-20 bg-gradient-to-b from-gray-50 to-white">
      {/* 컨테이너 - 콘텐츠 최대 너비 제한 및 중앙 정렬 */}
      <div className="container mx-auto px-6">

        {/* 제목 섹션 */}
        {/* text-center: 텍스트 가운데 정렬 */}
        {/* mb-12: 아래 마진 3rem (48px), 제목과 카드 사이 간격 */}
        <div className="text-center mb-12" ref={fadeInRef}>
          {/* 메인 제목 */}
          {/* text-3xl md:text-4xl: 모바일에서 3xl(1.875rem), 중간 화면 이상에서 4xl(2.25rem) */}
          {/* font-bold: 굵은 글씨 */}
          {/* text-gray-800: 진한 회색 텍스트 */}
          {/* mb-4: 아래 마진 1rem (16px) */}
          <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">
            소득 공제혜택 실제 사례
          </h2>

          {/* 부제목/설명 */}
          {/* text-base md:text-lg: 모바일에서 기본 크기(1rem), 중간 화면 이상에서 lg(1.125rem) */}
          {/* text-gray-600: 중간 회색, 부제목이므로 메인 텍스트보다 연하게 */}
          {/* max-w-3xl: 최대 너비 48rem (768px)로 제한하여 가독성 향상 */}
          {/* mx-auto: 좌우 마진 auto로 중앙 정렬 */}
          <p className="text-base md:text-lg text-gray-600 max-w-3xl mx-auto">
            실제로 벤처투자를 통해 절세 효과를 누리고 계신 고객님들의 사례입니다.
            <br className="hidden md:block" />
            {/* hidden md:block: 모바일에서는 숨김, 중간 화면 이상에서 표시 */}
            소득과 투자 금액에 따라 달라지는 실제 세금 환급액을 확인해보세요.
          </p>
        </div>

        {/* 사례 카드 그리드 */}
        {/* grid: CSS 그리드 레이아웃 사용 */}
        {/* grid-cols-1: 모바일(기본)에서 1열 - 작은 화면에서 세로로 나열 */}
        {/* md:grid-cols-2: 중간 화면(768px 이상)에서 2열 - 태블릿에서 좌우 2개 */}
        {/* lg:grid-cols-3: 큰 화면(1024px 이상)에서 3열 - 데스크톱에서 3개 나란히 */}
        {/* gap-8: 그리드 아이템 사이 간격 2rem (32px) */}
        <div ref={cardsRef} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {cases.map((caseItem, index) => {
            const colors = getColorClasses(caseItem.color);

            return (
              // 개별 카드 컴포넌트
              // staggered-animation: 순차적으로 나타나는 애니메이션 (커스텀 CSS)
              // bg-white: 흰색 배경
              // rounded-2xl: 큰 모서리 둥글기 (1rem), 부드러운 느낌
              // shadow-lg: 큰 그림자 효과, 카드가 떠있는 느낌
              // overflow-hidden: 내부 콘텐츠가 카드 경계를 넘지 않도록
              // hover:shadow-xl: 마우스 올렸을 때 더 큰 그림자
              // transition-all: 모든 속성 변화에 부드러운 전환 효과
              // duration-300: 전환 효과 지속 시간 300ms
              <div
                key={index}
                className="staggered-animation bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300"
              >
                {/* 카드 헤더 - 그라디언트 배경 */}
                {/* bg-gradient-to-r: 왼쪽에서 오른쪽으로 그라디언트 */}
                {/* p-6: 패딩 1.5rem (24px), 내부 여백 */}
                {/* text-white: 흰색 텍스트 */}
                <div className={`bg-gradient-to-r ${colors.gradient} p-6 text-white`}>
                  {/* 아이콘과 제목을 담은 flexbox */}
                  {/* flex: flexbox 레이아웃 */}
                  {/* items-center: 수직 중앙 정렬 */}
                  {/* mb-2: 아래 마진 0.5rem (8px) */}
                  <div className="flex items-center mb-2">
                    {/* 아이콘 컨테이너 */}
                    {/* w-12 h-12: 너비와 높이 3rem (48px), 정사각형 */}
                    {/* rounded-full: 완전히 둥근 원 모양 */}
                    {/* flex items-center justify-center: 내부 아이콘을 정중앙에 배치 */}
                    {/* mr-3: 오른쪽 마진 0.75rem (12px), 아이콘과 텍스트 사이 간격 */}
                    <div className={`w-12 h-12 ${colors.bg} bg-opacity-30 rounded-full flex items-center justify-center mr-3`}>
                      {caseItem.icon}
                    </div>
                    {/* 케이스 제목 */}
                    {/* text-xl: 텍스트 크기 1.25rem */}
                    {/* font-bold: 굵은 글씨 */}
                    <h3 className="text-xl font-bold">{caseItem.title}</h3>
                  </div>

                  {/* 소득 정보 */}
                  {/* text-sm: 작은 텍스트 0.875rem */}
                  {/* opacity-90: 90% 투명도, 약간 연하게 */}
                  <p className="text-sm opacity-90">{caseItem.annualIncome}</p>

                  {/* 투자 금액 */}
                  {/* font-semibold: 세미볼드 (medium과 bold 중간) */}
                  <p className="text-sm font-semibold">{caseItem.investment}</p>
                </div>

                {/* 카드 본문 - 수치 정보 */}
                {/* p-6: 패딩 1.5rem (24px) */}
                {/* space-y-4: 자식 요소들 사이에 수직 간격 1rem (16px) */}
                <div className="p-6 space-y-4">

                  {/* 과세표준 섹션 */}
                  <div>
                    {/* 라벨 */}
                    {/* text-sm: 작은 텍스트 */}
                    {/* text-gray-500: 연한 회색, 라벨이므로 덜 강조 */}
                    {/* mb-1: 아래 마진 0.25rem (4px) */}
                    <p className="text-sm text-gray-500 mb-1">과세표준 (투자 전)</p>

                    {/* 금액 표시 */}
                    {/* text-3xl: 큰 텍스트 1.875rem, 주요 정보이므로 크게 */}
                    {/* font-bold: 굵은 글씨 */}
                    {/* colors.text: 각 케이스의 테마 색상 */}
                    <p className={`text-3xl font-bold ${colors.text}`}>
                      {caseItem.taxableIncomeInManwon.toLocaleString()}만원
                    </p>
                  </div>

                  {/* 구분선 */}
                  {/* border-t: 위쪽 테두리 */}
                  {/* border-gray-200: 연한 회색 테두리 */}
                  <div className="border-t border-gray-200"></div>

                  {/* 실제 절세액 섹션 */}
                  {/* bg-gradient-to-r: 왼쪽에서 오른쪽으로 그라디언트 배경 */}
                  {/* p-4: 패딩 1rem (16px) */}
                  {/* rounded-xl: 모서리 둥글기 0.75rem */}
                  <div className={`bg-gradient-to-r ${colors.light} p-4 rounded-xl`}>
                    <p className="text-sm text-gray-600 mb-2">
                      {/* 절세액 라벨 */}
                      실제 절세액
                    </p>

                    {/* flex: flexbox 레이아웃 */}
                    {/* items-baseline: 텍스트 기준선에 맞춰 정렬 */}
                    <div className="flex items-baseline">
                      {/* 절세액 표시 */}
                      {/* text-4xl: 매우 큰 텍스트 2.25rem, 가장 중요한 정보 */}
                      {/* font-bold: 굵은 글씨 */}
                      {/* text-gray-800: 진한 회색 */}
                      <span className="text-4xl font-bold text-gray-800">
                        {caseItem.taxSavings.toLocaleString()}
                      </span>
                      {/* 단위 표시 */}
                      {/* text-xl: 큰 텍스트 1.25rem */}
                      {/* ml-2: 왼쪽 마진 0.5rem (8px) */}
                      <span className="text-xl text-gray-700 ml-2">만원</span>
                    </div>
                  </div>

                  {/* 추가 설명 */}
                  {/* text-xs: 매우 작은 텍스트 0.75rem */}
                  {/* text-gray-500: 연한 회색 */}
                  {/* text-center: 중앙 정렬 */}
                  {/* mt-2: 위 마진 0.5rem (8px) */}
                  <p className="text-xs text-gray-500 text-center mt-2">
                    💡 투자금의 {((caseItem.taxSavings / caseItem.investmentAmountInManwon) * 100).toFixed(1)}%를 세금으로 돌려받습니다
                  </p>
                </div>
              </div>
            );
          })}
        </div>

        {/* 실제 사례 영수증 보기 버튼 */}
        {/* mt-12: 위 마진 3rem (48px), 카드와 충분한 간격 */}
        {/* text-center: 중앙 정렬 */}
        <div className="mt-12 text-center">
          {/* 버튼 */}
          {/* bg-gradient-to-r: 왼쪽에서 오른쪽으로 그라디언트 */}
          {/* from-blue-500 to-indigo-600: 파란색에서 남색으로 그라디언트 */}
          {/* text-white: 흰색 텍스트 */}
          {/* px-8: 좌우 패딩 2rem (32px) */}
          {/* py-4: 상하 패딩 1rem (16px) */}
          {/* rounded-xl: 큰 모서리 둥글기 */}
          {/* font-semibold: 세미볼드 */}
          {/* text-lg: 큰 텍스트 */}
          {/* hover:shadow-xl: 마우스 올렸을 때 큰 그림자 */}
          {/* transform hover:scale-105: 마우스 올렸을 때 5% 확대 */}
          {/* transition-all: 모든 속성 변화에 부드러운 전환 */}
          {/* duration-300: 전환 효과 300ms */}
          {/* inline-flex: inline flex 레이아웃 */}
          {/* items-center: 수직 중앙 정렬 */}
          <button
            onClick={() => setIsModalOpen(true)}
            className="bg-gradient-to-r from-blue-500 to-indigo-600 text-white px-8 py-4 rounded-xl font-semibold text-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300 inline-flex items-center"
          >
            {/* 아이콘 */}
            <svg className="w-6 h-6 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
            </svg>
            실제 사례 영수증 보기
          </button>
        </div>

        {/* 하단 안내 메시지 */}
        {/* mt-12: 위 마진 3rem (48px), 카드와 충분한 간격 */}
        {/* bg-blue-50: 연한 파란색 배경 */}
        {/* border-l-4: 왼쪽에 두꺼운 테두리 4px */}
        {/* border-blue-500: 파란색 테두리 */}
        {/* p-6: 패딩 1.5rem (24px) */}
        {/* rounded-lg: 모서리 둥글기 0.5rem */}
        <div className="mt-12 bg-blue-50 border-l-4 border-blue-500 p-6 rounded-lg">
          {/* flex: flexbox 레이아웃 */}
          {/* items-start: 위쪽 정렬 (아이콘과 텍스트 위치 맞춤) */}
          <div className="flex items-start">
            {/* 정보 아이콘 */}
            {/* flex-shrink-0: 공간이 부족해도 아이콘 크기 유지 */}
            {/* mr-3: 오른쪽 마진 0.75rem (12px) */}
            <svg className="w-6 h-6 text-blue-500 flex-shrink-0 mr-3" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd"/>
            </svg>
            {/* 안내 텍스트 컨테이너 */}
            <div>
              {/* 안내 제목 */}
              {/* text-sm md:text-base: 모바일에서 작게, 중간 화면 이상에서 기본 크기 */}
              {/* font-semibold: 세미볼드 */}
              {/* text-blue-800: 진한 파란색 */}
              {/* mb-2: 아래 마진 0.5rem (8px) */}
              <p className="text-sm md:text-base font-semibold text-blue-800 mb-2">
                📌 실제 절세액은 개인의 소득 구간과 기타 공제 항목에 따라 달라질 수 있습니다
              </p>
              {/* 상세 설명 */}
              {/* text-xs md:text-sm: 모바일에서 매우 작게, 중간 화면 이상에서 작은 크기 */}
              {/* text-blue-700: 중간 파란색 */}
              <p className="text-xs md:text-sm text-blue-700">
                • <strong>공제 한도:</strong> 벤처투자 소득공제는 해당 연도 종합소득금액의 50%까지만 적용됩니다<br />
                • 위 사례는 대표적인 예시이며, 정확한 절세 효과는 무료 상담을 통해 확인하실 수 있습니다<br />
                • 벤처투자 소득공제는 종합소득세 신고 시 자동으로 반영됩니다
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* 영수증 이미지 모달 */}
      {/* fixed: 고정 위치 (스크롤해도 위치 유지) */}
      {/* inset-0: top, right, bottom, left 모두 0 (전체 화면 덮기) */}
      {/* z-50: z-index 50 (다른 요소 위에 표시) */}
      {/* flex items-end: flexbox로 하단 정렬 (모바일용) */}
      {/* md:items-center: 중간 화면 이상에서는 중앙 정렬 */}
      {/* justify-center: 수평 중앙 정렬 */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-end md:items-center justify-center">
          {/* 배경 오버레이 */}
          {/* absolute inset-0: 전체 화면 덮기 */}
          {/* bg-black bg-opacity-60: 검은색 배경, 60% 투명도 */}
          {/* backdrop-blur-sm: 배경 블러 효과 */}
          {/* animate-fadeIn: 페이드인 애니메이션 */}
          <div
            className="absolute inset-0 bg-black bg-opacity-60 backdrop-blur-sm animate-fadeIn"
            onClick={() => setIsModalOpen(false)}
          />

          {/* 모달 컨텐츠 */}
          {/* relative: 상대 위치 (z-index 적용 위해) */}
          {/* bg-white: 흰색 배경 */}
          {/* rounded-t-3xl: 위쪽 모서리만 크게 둥글게 (모바일용) */}
          {/* md:rounded-2xl: 중간 화면 이상에서는 전체 둥글게 */}
          {/* shadow-2xl: 매우 큰 그림자 */}
          {/* w-full: 전체 너비 (모바일) */}
          {/* md:w-auto: 중간 화면 이상에서는 자동 너비 */}
          {/* max-w-4xl: 최대 너비 56rem (896px) */}
          {/* max-h-[90vh]: 최대 높이 화면의 90% */}
          {/* overflow-hidden: 내용 넘침 숨김 */}
          {/* animate-slideUp: 하단에서 슬라이드업 애니메이션 */}
          <div className="relative bg-white rounded-t-3xl md:rounded-2xl shadow-2xl w-full md:w-auto max-w-4xl max-h-[90vh] overflow-hidden animate-slideUp">

            {/* 헤더 */}
            {/* flex justify-between items-center: flexbox로 좌우 정렬 */}
            {/* p-6: 패딩 1.5rem (24px) */}
            {/* border-b: 아래쪽 테두리 */}
            {/* border-gray-200: 연한 회색 테두리 */}
            <div className="flex justify-between items-center p-6 border-b border-gray-200">
              <h3 className="text-2xl font-bold text-gray-800">실제 사례 영수증</h3>

              {/* X 버튼 */}
              {/* text-gray-500: 회색 텍스트 */}
              {/* hover:text-gray-700: 마우스 올렸을 때 진한 회색 */}
              {/* hover:bg-gray-100: 마우스 올렸을 때 연한 회색 배경 */}
              {/* rounded-full: 완전히 둥근 원 */}
              {/* p-2: 패딩 0.5rem (8px) */}
              {/* transition-colors: 색상 변화 전환 효과 */}
              <button
                onClick={() => setIsModalOpen(false)}
                className="text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-full p-2 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"/>
                </svg>
              </button>
            </div>

            {/* 이미지 컨테이너 */}
            {/* overflow-y-auto: 세로 스크롤 가능 */}
            {/* p-6: 패딩 1.5rem (24px) */}
            {/* max-h-[calc(90vh-100px)]: 최대 높이 (헤더 제외) */}
            <div className="overflow-y-auto p-6 max-h-[calc(90vh-100px)]">
              {/* 이미지 */}
              {/* w-full: 전체 너비 */}
              {/* h-auto: 높이 자동 (비율 유지) */}
              {/* rounded-lg: 모서리 둥글게 */}
              {/* shadow-md: 중간 그림자 */}
              <img
                src="/images/real.jpeg"
                alt="실제 절세 사례 영수증"
                className="w-full h-auto rounded-lg shadow-md"
                onError={(e) => {
                  e.target.src = 'https://via.placeholder.com/800x1000/E5E7EB/6B7280?text=영수증+이미지';
                }}
              />

              {/* 설명 텍스트 */}
              {/* mt-4: 위 마진 1rem (16px) */}
              {/* text-center: 중앙 정렬 */}
              {/* text-sm: 작은 텍스트 */}
              {/* text-gray-600: 중간 회색 */}
              <p className="mt-4 text-center text-sm text-gray-600">
                ※ 고객 개인정보 보호를 위해 일부 정보는 가려졌습니다
              </p>
            </div>
          </div>
        </div>
      )}

      {/* 애니메이션 스타일 */}
      <style>{`
        /* 페이드인 애니메이션 (배경 오버레이용) */
        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        .animate-fadeIn {
          animation: fadeIn 0.3s ease-out;
        }

        /* 슬라이드업 애니메이션 (모달용) */
        /* 모바일: 하단에서 위로 */
        @keyframes slideUp {
          from {
            transform: translateY(100%);
          }
          to {
            transform: translateY(0);
          }
        }

        .animate-slideUp {
          animation: slideUp 0.4s cubic-bezier(0.16, 1, 0.3, 1);
        }

        /* 데스크톱: 중앙에서 부드럽게 나타남 */
        @media (min-width: 768px) {
          @keyframes slideUpDesktop {
            from {
              transform: translateY(30px);
              opacity: 0;
            }
            to {
              transform: translateY(0);
              opacity: 1;
            }
          }

          .animate-slideUp {
            animation: slideUpDesktop 0.4s cubic-bezier(0.16, 1, 0.3, 1);
          }
        }

        /* 스크롤바 스타일 개선 */
        .overflow-y-auto::-webkit-scrollbar {
          width: 8px;
        }

        .overflow-y-auto::-webkit-scrollbar-track {
          background: #f1f1f1;
          border-radius: 10px;
        }

        .overflow-y-auto::-webkit-scrollbar-thumb {
          background: #888;
          border-radius: 10px;
        }

        .overflow-y-auto::-webkit-scrollbar-thumb:hover {
          background: #555;
        }
      `}</style>
    </section>
  );
};

export default TaxBenefitsExamples;
